import React, { useState, useEffect, useMemo } from 'react';
import { initMercadoPago, Payment } from '@mercadopago/sdk-react';
import { Loader2, AlertCircle } from 'lucide-react';
import api from '../api/axios';

initMercadoPago(import.meta.env.VITE_MP_PUBLIC_KEY, {
  locale: 'es-MX',
});

export default function CheckoutPayment({ shipmentData, amountToPay, onPaymentSuccess }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, []);
  
  const isDark = document.documentElement.classList.contains('dark');

  // 🛡️ FIX 1: useMemo congela el objeto para que no se reinicie el Brick
  const initialization = useMemo(() => ({
    amount: parseFloat(amountToPay) > 0 ? parseFloat(amountToPay) : 10,
  }), [amountToPay]);

  // 🛡️ FIX 2: Congelamos también la configuración visual
  const customization = useMemo(() => ({
    paymentMethods: {
      ticket: "all",
      creditCard: "all",
      debitCard: "all", 
    },
    visual: {
      style: {
        theme: isDark ? "dark" : "default",
        customVariables: {
          baseColor: "#CE010B",
          formBackgroundColor: isDark ? "#590000" : "#ffffff", 
          borderRadius: "16px",
          inputHeight: "56px",
          buttonHeight: "56px",
          fontSizeSmall: "14px",
          fontSizeMedium: "18px",
          fontSizeLarge: "22px",
          fontSizeExtraLarge: "26px",
        }
      }
    }
  }), [isDark]);

  const onSubmit = (brickData) => {
    return new Promise((resolve, reject) => {
      setIsProcessing(true);
      setErrorMessage(null);

      // 🛡️ FIX TOTAL PARA PRODUCCIÓN:
      // Mercado Pago a veces pone los datos dentro de 'formData' y a veces en la raíz.
      const rawData = brickData.formData || brickData;

      // 1. Extraemos token y método de pago asegurando compatibilidad
      const token = brickData.token || rawData.token;
      // Mercado Pago siempre provee 'payment_method_id' al identificar la tarjeta
      const paymentMethodId = brickData.payment_method_id || rawData.payment_method_id;
      // Importante para tarjetas de débito/crédito con múltiples emisores
      const issuerId = rawData.issuer_id || brickData.issuer_id; 

      // 2. Preparamos el payload exacto que tu PaymentController espera:
      const payload = {
        // Incluimos todo por seguridad, pero aseguramos los campos clave:
        ...brickData,
        token: token, 
        payment_method_id: paymentMethodId, // 🟢 ESTO CORRIGE EL ERROR 400
        issuer_id: issuerId, // Necesario para tarjetas
        
        // Datos de tu lógica local
        shipment_id: shipmentData.id,
        tracking_number: shipmentData.tracking_number,

        // Blindaje para el Payer con datos reales del remitente
        payer: rawData.payer || brickData.payer || {
          email: shipmentData?.sender_email || 'contacto@paqueteriamasenvios.com.mx',
          first_name: (shipmentData?.sender_name || 'Cliente').split(' ')[0],
          last_name: (shipmentData?.sender_name || 'Más Envíos').split(' ').slice(1).join(' ') || 'Cliente'
        }
      };

      api.post('/payments/process', payload)
      .then(response => {
        // Verificamos si el pago fue aprobado o quedó pendiente
        if (response.data.status === 'approved' || response.data.status === 'pending') {
          onPaymentSuccess({
            ...response.data,
            tienda_exacta: paymentMethodId || 'credit_card'
          });
          resolve();
        } else {
          // Si MP rechaza la transacción (ej: fondos insuficientes)
          setErrorMessage(response.data.detalle || "La transacción fue rechazada por Mercado Pago.");
          reject();
        }
      })
      .catch(error => {
        // Capturamos el error 500 o 400 del backend
        console.error("Error en el servidor TBT:", error.response?.data || error);
        
        // Mostramos el detalle exacto de MP si tu backend nos lo dio (ej: "payment_method_id can't be null")
        const mpError = error.response?.data?.detalle || error.response?.data?.error;
        const msg = mpError || "Error crítico al procesar el pago en el servidor.";
        
        setErrorMessage(msg);
        reject();
      })
      .finally(() => {
        setIsProcessing(false);
      });
    });
  };

  return (
    <div className="relative w-full p-4 md:p-6 transition-all duration-500">
      <h2 className="text-3xl lg:text-2xl font-black text-gray-800 dark:text-white mb-6 uppercase tracking-tight">
        Finalizar Pago
      </h2>

      {errorMessage && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-black/40 border-l-4 border-red-600 flex items-start gap-3 animate-pulse">
          <AlertCircle className="text-red-600 dark:text-white shrink-0" size={20} />
          <p className="text-sm font-bold text-red-800 dark:text-white leading-tight">
            {errorMessage}
          </p>
        </div>
      )}

      <div className={isProcessing ? 'opacity-20 pointer-events-none' : ''}>
        {mounted && (
          <Payment
            key={isDark ? 'dark-mode' : 'light-mode'}
            initialization={initialization}
            customization={customization}
            onSubmit={onSubmit}
            onError={(error) => {
              console.error("Error SDK Mercado Pago:", error);
              // Ya no disparamos la alerta roja por cualquier cosa, solo lo anotamos en consola
              // para evitar que un reinicio del iframe bloquee la vista.
            }}
          />
        )}
      </div>
      
      {isProcessing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 dark:bg-black/60 rounded-xl backdrop-blur-[2px] z-10">
           <Loader2 className="animate-spin text-[#CE010B] dark:text-white mb-3" size={48} />
           <p className="font-bold text-gray-900 dark:text-white uppercase tracking-widest text-base lg:text-sm">
             Validando Transacción...
           </p>
        </div>
      )}
    </div>
  );
}